
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { DollarSign, CreditCard, Truck, Package, Handshake, BadgeCheck, ClipboardList } from "lucide-react";

import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  Stack,
  alpha,
} from "@mui/material";


const brand = {
  primary: "#F2C94C",
  primaryDark: "#E6B73A",
  text: "#111827",
  subtext: "#4B5563",
  surface: "#FFFFFF",
  border: "rgba(17,24,39,0.06)",
  shadow: "0 10px 25px rgba(0,0,0,.08)",
};

const buyerDefault = [
  { icon: "💸", title: "Place Bids", desc: "Bid on verified scrap listings with confidence." },
  { icon: "💳", title: "Confirm & Pay", desc: "Escrow-backed, bank‑grade secure checkout." },
  { icon: "📦", title: "Receive Delivery", desc: "Trusted logistics to your yard or warehouse." },
];

const sellerDefault = [
  { icon: "📤", title: "List Scrap", desc: "Create listings with photos, specs, and volumes." },
  { icon: "🤝", title: "Accept Offers", desc: "Review buyer bids and seal the best deal." },
  { icon: "💰", title: "Ship & Get Paid", desc: "Pickup arranged; funds released on delivery." },
];

const pickIcon = (item, index) => {
  if (item && item.icon && React.isValidElement(item.icon)) return item.icon;
  const t = (item?.title || "").toLowerCase();
  let I = ClipboardList;
  if (t.includes("bid")) I = DollarSign;
  else if (t.includes("confirm") || t.includes("pay")) I = CreditCard;
  else if (t.includes("deliver") || t.includes("ship")) I = Truck;
  else if (t.includes("list")) I = Package;
  else if (t.includes("offer") || t.includes("accept")) I = Handshake;
  else if (t.includes("paid") || t.includes("verify")) I = BadgeCheck;
  else I = [DollarSign, CreditCard, Truck, Package, Handshake, BadgeCheck, ClipboardList][index % 7];
  return <I size={24} />;
};

export default function HowItWorksSection({ buyerSteps, sellerSteps }) {
  const [tab, setTab] = useState(0);
  const steps = useMemo(() => (tab === 0 ? buyerSteps || buyerDefault : sellerSteps || sellerDefault), [tab, buyerSteps, sellerSteps]);
  const viewSteps = useMemo(() => {
    const src = Array.isArray(steps) ? steps : [];
    if (src.length >= 7) return src.slice(0, 7);
    const out = [];
    for (let i = 0; i < 7; i++) out.push(src[i % Math.max(src.length, 1)]);
    return out;
  }, [steps]);
  const indicatorRef = useRef(null);
  const tabsWrapRef = useRef(null);
  const buyerRef = useRef(null);
  const sellerRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const el = indicatorRef.current;
      const activeEl = tab === 0 ? buyerRef.current : sellerRef.current;
      if (!el || !activeEl) return;
      const left = activeEl.offsetLeft;
      const width = activeEl.offsetWidth;
      el.style.left = `${left}px`;
      el.style.width = `${width}px`;
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [tab]);

  useEffect(() => {
    // restart reveal animation when switching tab
    document.querySelectorAll(".hiwp-card").forEach((n, i) => {
      n.classList.remove("hiwp-in");
      // force reflow
      // @ts-ignore
      void n.offsetWidth;
      n.style.setProperty("--d", `${i * 90}ms`);
      n.classList.add("hiwp-in");
    });
  }, [tab, steps.length]);

  return (
    <section id="how-it-works" style={{ position: "relative", padding: "84px 0", background: "linear-gradient(180deg, rgba(242,201,76,.10) 0%, rgba(255,255,255,0) 55%)" }}>
      {/* <style>
        {`
        .hiwp-wrap{max-width:1120px;margin:0 auto;padding:0 16px;position:relative}
        .hiwp-title{font-weight:800;letter-spacing:-.02em;font-size:clamp(28px,4vw,40px);text-align:center;color:${brand.text}}
        .hiwp-sub{max-width:780px;margin:10px auto 28px;text-align:center;color:${brand.subtext};font-size:clamp(14px,1.6vw,16px)}
        .hiwp-tabs{display:flex;justify-content:center;gap:20px;margin-bottom:30px;position:relative}
        .hiwp-pill{width:120px;display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:10px 14px;font-weight:700;font-size:14px;color:${brand.text};background:transparent;cursor:pointer;user-select:none}
        .hiwp-pill.active{color:#111;background:#fff;box-shadow:0 8px 20px rgba(0,0,0,.08)}
        .hiwp-ind{position:absolute;left:0;bottom:-12px;height:4px;width:120px;border-radius:999px;background:linear-gradient(90deg, ${brand.primary}, ${brand.primaryDark});transition:left .35s cubic-bezier(.22,1,.36,1), width .35s cubic-bezier(.22,1,.36,1);pointer-events:none}
        .hiwp-slider{position:relative;overflow-x:auto;scroll-snap-type:x mandatory;scroll-behavior:smooth;padding:6px 4px 2px 4px;gap:18px;display:flex;cursor:grab}
        .hiwp-slider::-webkit-scrollbar{display:none}
        .hiwp-slider:active{cursor:grabbing}
        .hiwp-card{scroll-snap-align:start;flex:0 0 clamp(240px,30vw,300px);position:relative;border-radius:18px;background:#fff;border:1px solid ${brand.border};box-shadow:${brand.shadow};padding:18px 16px;overflow:hidden;transition:transform .25s ease, box-shadow .25s ease;margin-right:18px}
        .hiwp-card:last-child{margin-right:0}
        .hiwp-card:hover{transform:translateY(-6px)}
        .hiwp-in{animation:hiwpFade .6s ease var(--d,0ms) both}
        .hiwp-icon{height:60px;width:60px;margin:0 auto;border-radius:16px;display:grid;place-items:center;color:#fff;font-size:28px;box-shadow:0 8px 22px rgba(0,0,0,.12);background:linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)}
        .hiwp-num{position:absolute;top:14px;right:14px;height:28px;min-width:28px;padding:0 8px;border-radius:999px;background:#111;color:#fff;font-weight:800;display:grid;place-items:center;font-size:12px;letter-spacing:.02em}
        .hiwp-name{text-align:center;margin-top:12px;font-weight:800;color:${brand.text};font-size:16px}
        .hiwp-desc{text-align:center;margin-top:8px;color:${brand.subtext};font-size:14px;line-height:1.6}
        .hiwp-line{display:none}
        .hiwp-dots{display:flex;justify-content:center;gap:10px;margin-top:18px}
        .hiwp-dot{height:8px;width:8px;border-radius:999px;background:#D1D5DB;cursor:pointer;transition:all .2s ease}
        .hiwp-dot.active{background:${brand.primary};width:24px}
        @keyframes hiwpFade{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}
      `}</style> */}


      <style>{`
        html, body {
          overflow-x: hidden; /*  remove horizontal scrollbar */
          overflow-y: auto;   /*  keep only vertical scrolling for page content */
          margin: 0;
          padding: 0;
        }

        .hiwp-wrap {
          max-width: 1120px;
          margin: 0 auto;
          padding: 0 16px;
          position: relative;
          overflow: hidden; /*  contain children */
        }

        .hiwp-title {
          font-weight: 800;
          letter-spacing: -0.02em;
          font-size: clamp(28px, 4vw, 40px);
          text-align: center;
          color: ${brand.text};
        }

        .hiwp-sub {
          max-width: 780px;
          margin: 10px auto 28px;
          text-align: center;
          color: ${brand.subtext};
          font-size: clamp(14px, 1.6vw, 16px);
        }

        .hiwp-tabs {
          display: flex;
          justify-content: center;
          gap: 20px;
          margin-bottom: 30px;
          position: relative;
        }

        .hiwp-pill {
          width: 120px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          padding: 10px 14px;
          font-weight: 700;
          font-size: 14px;
          color: ${brand.text};
          background: transparent;
          cursor: pointer;
          user-select: none;
        }

        .hiwp-pill.active {
          color: #111;
          background: #fff;
          box-shadow: 0 8px 20px rgba(0,0,0,.08);
        }

        .hiwp-ind {
          position: absolute;
          left: 0;
          bottom: -12px;
          height: 4px;
          width: 120px;
          border-radius: 999px;
          background: linear-gradient(90deg, ${brand.primary}, ${brand.primaryDark});
          transition: left .35s cubic-bezier(.22,1,.36,1), width .35s cubic-bezier(.22,1,.36,1);
          pointer-events: none;
        }

        .hiwp-slider {
          position: relative;
          display: flex;
          gap: 18px;
          padding: 6px 4px 2px 4px;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          cursor: grab;

          overflow-x: auto;
          overflow-y: hidden; /*  stop vertical scrollbar */
        }

        .hiwp-slider::-webkit-scrollbar {
          display: none; /*  hide scrollbar */
        }

        .hiwp-slider:active {
          cursor: grabbing;
        }

        .hiwp-card {
          scroll-snap-align: start;
          flex: 0 0 clamp(240px, 30vw, 300px);
          position: relative;
          border-radius: 18px;
          background: #fff;
          border: 1px solid ${brand.border};
          box-shadow: ${brand.shadow};
          padding: 18px 16px;
          overflow: hidden;
          transition: transform .25s ease, box-shadow .25s ease;
          margin-right: 18px;
        }

        .hiwp-card:last-child {
          margin-right: 0;
        }

        .hiwp-card:hover {
          transform: translateY(-6px);
        }

        .hiwp-in {
          animation: hiwpFade .6s ease var(--d,0ms) both;
        }

        .hiwp-icon {
          height: 60px;
          width: 60px;
          margin: 0 auto;
          border-radius: 16px;
          display: grid;
          place-items: center;
          color: #fff;
          font-size: 28px;
          box-shadow: 0 8px 22px rgba(0,0,0,.12);
          background: linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%);
        }

        .hiwp-num {
          position: absolute;
          top: 14px;
          right: 14px;
          height: 28px;
          min-width: 28px;
          padding: 0 8px;
          border-radius: 999px;
          background: #111;
          color: #fff;
          font-weight: 800;
          display: grid;
          place-items: center;
          font-size: 12px;
          letter-spacing: .02em;
        }

        .hiwp-name {
          text-align: center;
          margin-top: 12px;
          font-weight: 800;
          color: ${brand.text};
          font-size: 16px;
        }

        .hiwp-desc {
          text-align: center;
          margin-top: 8px;
          color: ${brand.subtext};
          font-size: 14px;
          line-height: 1.6;
        }

        .hiwp-line {
          display: none;
        }

        .hiwp-dots {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 18px;
        }

        .hiwp-dot {
          height: 8px;
          width: 8px;
          border-radius: 999px;
          background: #D1D5DB;
          cursor: pointer;
          transition: all .2s ease;
        }

        .hiwp-dot.active {
          background: ${brand.primary};
          width: 24px;
        }

        @keyframes hiwpFade {
          from {
            opacity: 0;
            transform: translateY(10px) scale(.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>

      <div className="hiwp-wrap">
        <h3 className="hiwp-title">How It Works</h3>
        <p className="hiwp-sub">A sleek, step‑by‑step flow for both buyers and sellers. Clear actions, secure payments, and on‑time logistics.</p>

        <div ref={tabsWrapRef} className="hiwp-tabs" role="tablist" aria-label="How it works tabs">
          <div ref={buyerRef} className={`hiwp-pill ${tab===0?"active":""}`} role="tab" aria-selected={tab===0} onClick={()=>setTab(0)}>For Buyers</div>
          <div ref={sellerRef} className={`hiwp-pill ${tab===1?"active":""}`} role="tab" aria-selected={tab===1} onClick={()=>setTab(1)}>For Sellers</div>
          <span ref={indicatorRef} className="hiwp-ind" aria-hidden="true" />
        </div>

        <Slider key={tab} steps={viewSteps} />
      </div>
    </section>
  );
}

function Slider({ steps }) {
  const ref = useRef(null);
  const cardsRef = useRef([]);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  const timer = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => {
      // Find closest card index based on scrollLeft to keep dots in sync
      const cards = cardsRef.current.filter(Boolean);
      if (!cards.length) return;
      const offsets = cards.map((c) => c.offsetLeft);
      const cur = offsets.reduce((best, off, i) => (
        Math.abs(off - el.scrollLeft) < Math.abs(offsets[best] - el.scrollLeft) ? i : best
      ), 0);
      setIdx(cur);
    };
    el.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => el.removeEventListener('scroll', handler);
  }, [steps.length]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const tick = () => {
      if (paused.current) return;
      const next = (idx + 1) % steps.length;
      const target = cardsRef.current[next];
      if (target) {
        el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        setIdx(next); // ensure wrap and continue after last
      }
    };
    timer.current = setInterval(tick, 3500);
    return () => clearInterval(timer.current);
  }, [idx, steps.length]);

  const go = (i) => {
    const el = ref.current;
    const card = cardsRef.current[i];
    if (!el || !card) return;
    el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
  };

  return (
    <div>
      <div
        ref={ref}
        className="hiwp-slider"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
      >
        {steps.map((s, i) => (
          <div
            key={(s.title||'step')+i}
            ref={(el) => (cardsRef.current[i] = el)}
            className="hiwp-card hiwp-in"
            style={{ animationDelay: `${i*90}ms` }}
          >
            <span className="hiwp-num">{String(i+1).padStart(2,'0')}</span>
            <div className="hiwp-icon" aria-hidden>{pickIcon(s, i)}</div>
            <div className="hiwp-name">{s.title}</div>
            <div className="hiwp-desc">{s.desc}</div>
            <span className="hiwp-line"/>
          </div>
        ))}
      </div>
      <div className="hiwp-dots" aria-label="Slider pagination">
        {steps.map((_, i) => (
          <span key={i} className={`hiwp-dot ${idx===i?'active':''}`} onClick={() => go(i)} />
        ))}
      </div>
    </div>
  );
}
